import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScenarioThumbnailField } from "@/components/scenarios/ScenarioThumbnailField";
import { RichTextEditor, isHtmlContentEmpty } from "@/components/ui/rich-text-editor";
import {
  useGetExerciseFormMetaQuery,
  useGetScenarioDetailQuery,
  useUpdateExerciseMutation,
  type CreateExercisePayload,
} from "@/store/apiSlice";

export default function ScenarioEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const skip = !id;

  const { data: meta, isLoading: metaLoading, error: metaError } = useGetExerciseFormMetaQuery(undefined, {
    skip,
  });
  const {
    data: detail,
    isLoading: detailLoading,
    isError: detailError,
    error: detailErr,
  } = useGetScenarioDetailQuery(id!, { skip });
  const [updateExercise, { isLoading: saving }] = useUpdateExerciseMutation();

  const [name, setName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [typeId, setTypeId] = useState("");
  const [severityId, setSeverityId] = useState("");
  const [modeId, setModeId] = useState("");
  const [description, setDescription] = useState("");
  const [objective, setObjective] = useState("");
  const [prerequisite, setPrerequisite] = useState("");
  const [hydrated, setHydrated] = useState(false);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);

  useEffect(() => {
    setHydrated(false);
    setThumbnailFile(null);
  }, [id]);

  useEffect(() => {
    if (!detail || !meta) return;

    setName(detail.name ?? "");
    setCategoryId(detail.category?.id ? String(detail.category.id) : "");
    setTypeId(detail.type?.id ? String(detail.type.id) : "");

    const sevName = typeof detail.severity === "string" ? detail.severity : "";
    const matchedSev = sevName ? meta.severities.find((s) => s.name === sevName) : undefined;
    setSeverityId(matchedSev ? String(matchedSev.id) : "");

    setModeId(detail.mode?.id ? String(detail.mode.id) : "");

    setDescription(detail.description ?? "");
    setObjective(detail.objective ?? "");
    setPrerequisite(detail.prerequisite ?? "");
    setHydrated(true);
  }, [detail, meta]);

  const canSubmit = useMemo(() => {
    const n = name.trim();
    const c = Number(categoryId);
    const t = Number(typeId);
    return Boolean(n && !Number.isNaN(c) && !Number.isNaN(t));
  }, [name, categoryId, typeId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || saving || !id) return;

    const payload: CreateExercisePayload = {
      name: name.trim(),
      category: Number(categoryId),
      type: Number(typeId),
    };
    const s = Number(severityId);
    payload.severity = severityId && !Number.isNaN(s) ? s : null;
    const m = Number(modeId);
    payload.mode = modeId && !Number.isNaN(m) ? m : null;
    payload.description = isHtmlContentEmpty(description) ? "" : description.trim();
    payload.objective = isHtmlContentEmpty(objective) ? "" : objective.trim();
    payload.prerequisite = isHtmlContentEmpty(prerequisite) ? "" : prerequisite.trim();
    if (thumbnailFile) payload.thumbnail = thumbnailFile;

    try {
      await updateExercise({ id, body: payload }).unwrap();
      toast.success("Scenario updated.");
      navigate(`/scenarios/${id}/detail`, { replace: true });
    } catch (err: unknown) {
      const anyErr = err as { data?: Record<string, unknown>; status?: number };
      const detailMsg =
        typeof anyErr?.data?.detail === "string"
          ? anyErr.data.detail
          : typeof anyErr?.data === "object" && anyErr.data
            ? JSON.stringify(anyErr.data)
            : "Failed to update scenario.";
      toast.error(detailMsg);
    }
  };

  if (!id) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
        <p className="font-medium text-destructive">Invalid scenario.</p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/scenarios">Back to scenarios</Link>
        </Button>
      </div>
    );
  }

  if (metaLoading || detailLoading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground gap-2">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading scenario…
      </div>
    );
  }

  if (metaError || !meta) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
        <p className="font-medium text-destructive">Could not load form options.</p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/scenarios">Back to scenarios</Link>
        </Button>
      </div>
    );
  }

  if (detailError || !detail) {
    const msg =
      detailErr && typeof detailErr === "object" && "data" in detailErr
        ? String((detailErr as { data?: { detail?: string } }).data?.detail ?? "Scenario not found.")
        : "Scenario not found.";
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
        <p className="font-medium text-destructive">{msg}</p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/scenarios">Back to scenarios</Link>
        </Button>
      </div>
    );
  }

  if (!hydrated) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground gap-2">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading scenario…
      </div>
    );
  }

  const selectClass =
    "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

  return (
    <div className="space-y-6 ">
      <div>
        <Button variant="ghost" size="sm" asChild className="mb-2 -ml-2">
          <Link to="/scenarios" className="text-muted-foreground">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to scenarios
          </Link>
        </Button>
        <h1 className="text-3xl font-bold tracking-tight">Edit scenario</h1>
        <p className="text-muted-foreground mt-1">
          Update exercise details. Open the scenario detail page for infrastructure, teams, and flags.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Exercise details</CardTitle>
          <CardDescription>Required fields are marked. Changes apply when you save.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-ex-name">Name *</Label>
              <Input
                id="edit-ex-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Scenario title"
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-ex-cat">Category *</Label>
                <select
                  id="edit-ex-cat"
                  className={selectClass}
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  required
                >
                  <option value="">Select category</option>
                  {meta.categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-ex-type">Exercise type *</Label>
                <select
                  id="edit-ex-type"
                  className={selectClass}
                  value={typeId}
                  onChange={(e) => setTypeId(e.target.value)}
                  required
                >
                  <option value="">Select type</option>
                  {meta.types.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.kind})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-ex-sev">Severity</Label>
                <select
                  id="edit-ex-sev"
                  className={selectClass}
                  value={severityId}
                  onChange={(e) => setSeverityId(e.target.value)}
                >
                  <option value="">— Optional —</option>
                  {meta.severities.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-ex-mode">Mode</Label>
                <select
                  id="edit-ex-mode"
                  className={selectClass}
                  value={modeId}
                  onChange={(e) => setModeId(e.target.value)}
                >
                  <option value="">— Optional —</option>
                  {meta.modes.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name} ({m.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <ScenarioThumbnailField
              id="edit-ex-thumbnail"
              existingUrl={detail.thumbnail_url || null}
              value={thumbnailFile}
              onChange={setThumbnailFile}
              disabled={saving}
            />

            <div className="space-y-2">
              <Label>Description</Label>
              <RichTextEditor
                value={description}
                onChange={setDescription}
                placeholder="Short description"
                minEditorHeight="8rem"
              />
            </div>
            <div className="space-y-2">
              <Label>Objective</Label>
              <RichTextEditor
                value={objective}
                onChange={setObjective}
                placeholder="Learning objectives"
                minEditorHeight="8rem"
              />
            </div>
            <div className="space-y-2">
              <Label>Prerequisite</Label>
              <RichTextEditor
                value={prerequisite}
                onChange={setPrerequisite}
                placeholder="Prerequisites for participants"
                minEditorHeight="6rem"
              />
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <Button type="submit" disabled={!canSubmit || saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save changes
              </Button>
              <Button type="button" variant="outline" asChild>
                <Link to={`/scenarios/${id}/detail`}>Cancel</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
